/**
 * One avatar renderer for every surface.
 *
 * The three places a user sees their own picture (dashboard header, My Profile,
 * presence Settings) each used to derive it differently — one hardcoded a stock
 * photo, one read a localStorage mock, one showed initials — so the same account
 * appeared three different ways on one screen. They now share this.
 *
 * Initials are the fallback rather than a stock silhouette: a generic photo of a
 * stranger reads as "this is you" to anyone glancing at it, whereas initials are
 * honest that no picture has been set.
 */

import { useEffect, useState } from 'react'

/** Up to two initials. Falls back to '?' so the circle is never blank. */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')
}

/**
 * Only http(s) URLs are renderable.
 *
 * Mobile used to persist a bare filesystem path (`/data/user/0/.../avatar_1.jpg`)
 * as the avatar URL. Those rows still exist, and handing one to an `<img src>`
 * resolves it against our own origin — a guaranteed broken image on a page that
 * would otherwise have shown perfectly good initials.
 */
export function isRenderableAvatarUrl(url: string | null | undefined): boolean {
  if (!url) return false
  return /^https?:\/\//i.test(url.trim())
}

export interface UserAvatarProps {
  name?: string | null
  avatarUrl?: string | null
  /** Rendered size in px. Drives the font size of the initials too. */
  size?: number
  className?: string
  /** Optional ring/border classes applied to both the image and the fallback. */
  frameClassName?: string
}

export function UserAvatar({
  name,
  avatarUrl,
  size = 40,
  className = '',
  frameClassName = '',
}: UserAvatarProps) {
  // A URL that 404s (deleted object, stale CDN entry) must degrade to initials
  // rather than a broken-image glyph. Keyed on the URL so a new upload retries.
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [avatarUrl])

  const showImage = isRenderableAvatarUrl(avatarUrl) && !failed
  const box = { width: size, height: size }

  if (showImage) {
    return (
      <img
        src={avatarUrl!}
        alt={name ?? 'Profile picture'}
        style={box}
        onError={() => setFailed(true)}
        className={`rounded-full object-cover shrink-0 ${frameClassName} ${className}`}
      />
    )
  }

  return (
    <div
      style={{ ...box, fontSize: Math.max(10, Math.round(size * 0.4)) }}
      aria-label={name ?? 'Profile picture'}
      className={`rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center shrink-0 select-none ${frameClassName} ${className}`}
    >
      {initialsOf(name)}
    </div>
  )
}

export default UserAvatar
